import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import Tabs from '@cloudscape-design/components/tabs';
import Table, { TableProps } from '@cloudscape-design/components/table';
import Pagination from '@cloudscape-design/components/pagination';
import Button from '@cloudscape-design/components/button';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Badge from '@cloudscape-design/components/badge';
import Spinner from '@cloudscape-design/components/spinner';
import Box from '@cloudscape-design/components/box';
import Select from '@cloudscape-design/components/select';
import Icon from '@cloudscape-design/components/icon';
import TextFilter from '@cloudscape-design/components/text-filter';
import FormField from '@cloudscape-design/components/form-field';
import Container from '@cloudscape-design/components/container';
import Modal from '@cloudscape-design/components/modal';
import Tiles from '@cloudscape-design/components/tiles';
import CollectionPreferences from '@cloudscape-design/components/collection-preferences';
import Alert from '@cloudscape-design/components/alert';
import Checkbox from '@cloudscape-design/components/checkbox';

import { useStorageInventory } from '@/hooks/useStorageInventory';
import { displayOptionalField, formatDate } from '@/utils/assetUtils';
import { getWarrantyStatus } from '@/utils/warrantyUtils';
import { filterByWarranty } from '@/utils/warrantyUtils';
import { exportToCSV, getExportFilename } from '@/utils/csvExport';
import { paginateItems, getTotalPages, sortItems } from '@/utils/paginationUtils';
import { applyAllFilters } from '@/utils/filterUtils';
import type {
  AssetCategory,
  Asset,
  ServerAsset,
  ElementalAsset,
  NetworkDeviceAsset,
  AssetStatus,
} from '@/types/storageInventory';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS, DEFAULT_ASSET_STATUSES } from '@/types/storageInventory';
import { AssetFormModal } from '@/components/storage-inventory/AssetFormModal';
import { AssetDetailPanel } from '@/components/storage-inventory/AssetDetailPanel';
import { SettingsPanel, StorageSettings } from '@/components/storage-inventory/SettingsPanel';
import { BulkImportModal } from '@/components/storage-inventory/BulkImportModal';
import { BulkAddPanel } from '@/components/storage-inventory/BulkAddPanel';
import { PhotoImage } from '@/components/storage-inventory/PhotoImage';
import { uploadPhotoFiles } from '@/lib/photoUpload';

// Column definitions for CSV export
const SERVER_COLUMNS = [
  'site', 'hostname', 'make', 'model', 'service_tag', 'express_service_code', 'ship_date',
  'support_end_date', 'status', 'cpu_model', 'cpu_quantity',
  'ram_model', 'ram_quantity', 'disk_model', 'disk_quantity',
  'last_known_contact',
];

const ELEMENTAL_COLUMNS = [
  'site', 'series', 'manufacturer', 'model', 'service_tag', 'express_service_code',
  'boxed_status', 'condition', 'status', 'location', 'to_be_decommissioned',
  'last_known_contact', 'purchase_order', 'cost', 'location_detail',
];

const NETWORK_DEVICE_COLUMNS = [
  'site', 'hostname', 'make', 'model', 'serial_num', 'support_end_date',
  'status', 'customer_order', 'shipping_box', 'tracking_num',
  'quantity', 'last_known_contact', 'purchase_order', 'cost', 'location_detail',
];

const CATEGORY_EXPORT_NAMES: Record<AssetCategory, string> = {
  servers: 'servers',
  elementals: 'elementals',
  network_devices: 'network-devices',
};

// Searchable fields per category for text search
const SEARCHABLE_FIELDS: Record<AssetCategory, string[]> = {
  servers: ['hostname', 'make', 'model', 'service_tag', 'express_service_code', 'notes'],
  elementals: ['site', 'series', 'manufacturer', 'model', 'service_tag', 'express_service_code', 'notes'],
  network_devices: ['hostname', 'make', 'model', 'serial_num', 'notes'],
};

// Default sites (fallback when settings has no `sites` list). Canonical
// `CODE (City)` names per InfraDev-1803. Assets whose site is not in this list
// still surface via the "⚠ Other / Unknown" site filter option.
const DEFAULT_SITES = [
  'EDM-ATL (Atlanta)',
  'EDM-LHR (London)',
  'H5-SEA (Seattle)',
  'IMG-LHR (London)',
  'LAX35 (Culver City)',
  'SBJ-NRT (Tokyo)',
  'SWITCH-LAX (Los Angeles)',
  'VID30-DEN (Denver)',
];

// Status dropdown values are configured in Settings (settings.statuses) and seeded
// from DEFAULT_ASSET_STATUSES, so every status dropdown draws from one source.
// Older stored settings may predate the `statuses` field; backfill it with the
// defaults so every status dropdown always has options.
function withStatusDefaults(s: unknown): StorageSettings {
  if (!s || typeof s !== 'object') return s as StorageSettings;
  const obj = s as StorageSettings;
  return {
    ...obj,
    statuses: Array.isArray(obj.statuses) && obj.statuses.length > 0 ? obj.statuses : DEFAULT_ASSET_STATUSES,
  };
}

// Warranty expiry filter options
const WARRANTY_FILTER_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Expiring in 30 days', value: '30' },
  { label: 'Expiring in 60 days', value: '60' },
  { label: 'Expiring in 90 days', value: '90' },
  { label: 'Already expired', value: 'expired' },
];

function renderStatusBadge(status: AssetStatus) {
  switch (status) {
    case 'in storage':
      return <StatusIndicator type="info">{status}</StatusIndicator>;
    case 'in production':
      return <StatusIndicator type="success">{status}</StatusIndicator>;
    case 'has issues':
      return <StatusIndicator type="error">{status}</StatusIndicator>;
    case 'decommissioned':
      return <StatusIndicator type="stopped">{status}</StatusIndicator>;
    case 'Missing/Unknown Can Not Find During Audit (Infradev-1803)':
      return <StatusIndicator type="warning">{status}</StatusIndicator>;
    case 'unknown':
    default:
      return <Badge color="grey">{status}</Badge>;
  }
}

function renderWarrantyIndicator(supportEndDate: string | undefined) {
  if (!supportEndDate) return null;
  const status = getWarrantyStatus(supportEndDate);
  switch (status) {
    case 'expired':
      return <Icon name="status-negative" variant="error" />;
    case 'expiring-soon':
      return <Icon name="status-warning" variant="warning" />;
    default:
      return null;
  }
}

export function StorageInventory() {
  const { servers, elementals, networkDevices, isLoading, error, addAsset, updateAsset, deleteAsset } =
    useStorageInventory();
  const [searchParams, setSearchParams] = useSearchParams();


  // Settings state
  const [settings, setSettings] = useState<StorageSettings>({
    sites: DEFAULT_SITES,
    server_makes: ['Dell', 'AJA', 'HP', 'Supermicro'],
    elemental_manufacturers: ['AWS'],
    network_makes: ['Cisco', 'Juniper', 'Arista'],
    server_models: ['PE R640', 'PE R650xs', 'PE R660xs', 'PE R440', 'PE R450', 'Ki Pro Ultra 12G'],
    elemental_series: ['700', '800', '900'],
    elemental_models: ['L732AE', 'L882AE', 'L885AE', 'L981AE-U'],
    network_models: ['Nexus 93180', 'qfx5120-48y-af12', 'ex3400-48t-afi'],
    statuses: DEFAULT_ASSET_STATUSES,
    warranty_alert_days: 30,
    default_page_size: 25,
    default_status: 'unknown',
    default_tab: 'servers',
  });

  // Load settings via the proxy backend if enabled, else localStorage/JSON fallback
  useEffect(() => {
    const useBackend = import.meta.env.VITE_STORAGE_INVENTORY_LOCAL === 'true' || !import.meta.env.DEV;

    if (useBackend) {
      // Load from the inventory API (local SQLite in dev, deployed API in prod)
      fetch('/inventory/scan?category=settings')
        .then((res) => res.ok ? res.json() : null)
        .then((data) => {
          // Settings are stored as a single object. If the endpoint returns an
          // array with one item, unwrap it.
          if (data && !Array.isArray(data)) {
            setSettings(withStatusDefaults(data));
          } else if (Array.isArray(data) && data.length > 0) {
            setSettings(withStatusDefaults(data[0]));
          }
        })
        .catch(() => {
          // Fallback to localStorage if the backend is unreachable
          const stored = localStorage.getItem('storage-inventory-settings');
          if (stored) { try { setSettings(withStatusDefaults(JSON.parse(stored))); } catch { /* ignore corrupt cached settings */ } }
        });
    } else {
      // Dev mode without the local backend: use localStorage, then static JSON
      const stored = localStorage.getItem('storage-inventory-settings');
      if (stored) {
        try { setSettings(withStatusDefaults(JSON.parse(stored))); return; } catch { /* ignore corrupt cached settings */ }
      }
      fetch('/data/storage-inventory/settings.json')
        .then((res) => res.ok ? res.json() : null)
        .then((data) => { if (data) setSettings(withStatusDefaults(data)); })
        .catch(() => {});
    }
  }, []);

  const handleSaveSettings = (newSettings: StorageSettings) => {
    setSettings(newSettings);
    // Always persist to localStorage as a fast cache
    localStorage.setItem('storage-inventory-settings', JSON.stringify(newSettings));

    // If the backend is enabled, also persist it there
    const useBackend = import.meta.env.VITE_STORAGE_INVENTORY_LOCAL === 'true' || !import.meta.env.DEV;
    if (useBackend) {
      fetch('/inventory/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category: 'settings', item: { id: 'settings', ...newSettings } }),
      }).catch((err) => {
        console.error('Failed to save settings:', err);
      });
    }
  };


  // Tab state
  const [activeTab, setActiveTab] = useState<AssetCategory | 'images' | 'settings' | 'bulk_add'>(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['servers', 'elementals', 'network_devices', 'images', 'settings', 'bulk_add'].includes(tabParam)) {
      return tabParam as AssetCategory | 'images' | 'settings' | 'bulk_add';
    }
    return 'servers';
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Sorting state
  const [sortingColumn, setSortingColumn] = useState<{ sortingField?: string } | null>(null);
  const [sortingDescending, setSortingDescending] = useState(false);

  // Modal state
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAsset, setEditingAsset] = useState<Asset | undefined>(undefined);
  const [editingCategory, setEditingCategory] = useState<AssetCategory>('servers');
  const [addCategoryPickerVisible, setAddCategoryPickerVisible] = useState(false);
  const [addCategory, setAddCategory] = useState<AssetCategory>('servers');
  const [bulkImportVisible, setBulkImportVisible] = useState(false);

  // Detail panel state
  const [detailPanelVisible, setDetailPanelVisible] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | undefined>(undefined);

  // Direct photo upload from table
  const photoInputRef = useRef<HTMLInputElement>(null);
  const photoTargetAssetRef = useRef<{ asset: Asset; category: AssetCategory } | null>(null);

  const handlePhotoColumnClick = useCallback((asset: Asset) => {
    const cat = activeTab !== 'images' && activeTab !== 'settings' && activeTab !== 'bulk_add' ? activeTab : 'servers';
    photoTargetAssetRef.current = { asset, category: cat as AssetCategory };
    photoInputRef.current?.click();
  }, [activeTab]);

  const handlePhotoInputChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    const target = photoTargetAssetRef.current;
    if (!files || files.length === 0 || !target) return;

    try {
      // Upload to S3 and store the returned keys on the asset.
      const keys = await uploadPhotoFiles(target.category, files);
      if (keys.length > 0) {
        const updated = { ...target.asset, images: [...(target.asset.images || []), ...keys] };
        updateAsset(target.category, updated as Asset);
      }
    } catch (err) {
      console.error('Photo upload failed', err);
    } finally {
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  }, [updateAsset]);

  // Deep link: open asset detail panel from URL param ?asset=UUID&tab=category
  useEffect(() => {
    if (isLoading) return;
    const assetId = searchParams.get('asset');
    if (!assetId) return;

    // Search across all categories
    const allAssets: { asset: Asset; category: AssetCategory }[] = [
      ...servers.map((a) => ({ asset: a as Asset, category: 'servers' as AssetCategory })),
      ...elementals.map((a) => ({ asset: a as Asset, category: 'elementals' as AssetCategory })),
      ...networkDevices.map((a) => ({ asset: a as Asset, category: 'network_devices' as AssetCategory })),
    ];
    const found = allAssets.find((item) => item.asset.id === assetId);
    if (found) {
      setActiveTab(found.category);
      setSelectedAsset(found.asset);
      setEditingCategory(found.category);
      setDetailPanelVisible(true);
    }
    // Clear the asset param after opening so refreshing doesn't re-open
    // (keep tab param)
  }, [isLoading, servers, elementals, networkDevices, searchParams]);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchField, setSearchField] = useState<string | null>(null);
  const [siteFilter, setSiteFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [warrantyFilter, setWarrantyFilter] = useState<string | null>(null);
  const [makeFilter, setMakeFilter] = useState<string | null>(null);
  const [modelFilter, setModelFilter] = useState<string | null>(null);
  const [showOnlyStorage, setShowOnlyStorage] = useState(true);

  // Full-workbook export (.xlsx backup) state
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // Column visibility preferences — default shows all columns
  const [visibleColumns, setVisibleColumns] = useState<Record<AssetCategory, string[]>>({
    servers: ['actions', 'has_image', 'site', 'make', 'model', 'service_tag', 'express_service_code', 'support_end_date', 'status', 'notes'],
    elementals: ['actions', 'has_image', 'site', 'series', 'manufacturer', 'model', 'service_tag', 'express_service_code', 'boxed_status', 'condition', 'status', 'location', 'notes'],
    network_devices: ['actions', 'has_image', 'site', 'make', 'model', 'serial_num', 'status', 'quantity', 'notes'],
  });

  // Get current category data
  const currentData = useMemo((): Asset[] => {
    switch (activeTab) {
      case 'servers':
        return servers;
      case 'elementals':
        return elementals;
      case 'network_devices':
        return networkDevices;
      case 'images':
      case 'settings':
      case 'bulk_add':
        return [];
      default:
        return [];
    }
  }, [activeTab, servers, elementals, networkDevices]);

  // Apply filters (text search by field, site, warranty) with AND logic
  const filteredData = useMemo((): Asset[] => {
    // Determine which fields to search based on searchField selection
    const searchableFields: string[] = searchField
      ? [searchField]
      : (SEARCHABLE_FIELDS[activeTab as AssetCategory] || []);

    // Apply text search and site filter. searchableFields is cast because the
    // union `keyof Asset` only covers keys common to every asset type, while we
    // search category-specific fields (e.g. hostname) by name at runtime.
    let result = applyAllFilters(currentData, {
      textQuery: searchQuery,
      searchableFields: searchableFields as unknown as (keyof Asset)[],
      site: siteFilter === '__other__' ? null : siteFilter,
      status: statusFilter,
    });

    // "Other / Unknown" filter: show only assets whose site is NOT in settings
    if (siteFilter === '__other__') {
      const settingSites = new Set(settings.sites || []);
      result = result.filter((asset) => !settingSites.has(asset.site));
    }

    // Then apply warranty filter if active
    if (warrantyFilter && warrantyFilter !== 'all') {
      result = filterByWarranty(result, warrantyFilter as 'expired' | '30' | '60' | '90');
    }

    // Show only "in storage" if toggle is checked
    if (showOnlyStorage) {
      result = result.filter((asset) => {
        const status = 'status' in asset ? asset.status : '';
        return status === 'in storage';
      });
    }

    return result;
  }, [currentData, searchQuery, searchField, siteFilter, statusFilter, warrantyFilter, showOnlyStorage, activeTab, settings.sites]);

  // Apply sorting
  const sortedData = useMemo(() => {
    if (!sortingColumn?.sortingField) return filteredData;
    return sortItems(
      filteredData,
      sortingColumn.sortingField as keyof Asset,
      sortingDescending ? 'descending' : 'ascending',
    );
  }, [filteredData, sortingColumn, sortingDescending]);

  // Apply pagination
  const paginatedData = useMemo(() => {
    return paginateItems(sortedData, currentPage, pageSize);
  }, [sortedData, currentPage, pageSize]);

  const totalPages = useMemo(() => {
    return getTotalPages(sortedData.length, pageSize);
  }, [sortedData.length, pageSize]);

  // Existing sites — from settings ONLY (settings is the single source of truth)
  const existingSites = useMemo(() => {
    return [...(settings.sites || [])].sort();
  }, [settings.sites]);
  // Existing makes — from settings ONLY, based on active category
  const existingMakes = useMemo(() => {
    const cat = activeTab !== 'images' && activeTab !== 'settings' && activeTab !== 'bulk_add' ? activeTab : 'servers';
    if (cat === 'servers') return [...(settings.server_makes || [])].sort();
    if (cat === 'elementals') return [...(settings.elemental_manufacturers || [])].sort();
    return [...(settings.network_makes || [])].sort();
  }, [activeTab, settings]);
  const sortModels = (models: string[]) =>
    [...models].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  const existingModels = useMemo(() => {
    // Use settings-based models per category
    const cat = activeTab !== 'images' && activeTab !== 'settings' && activeTab !== 'bulk_add' ? activeTab : 'servers';
    if (cat === 'servers') return sortModels(settings.server_models || []);
    if (cat === 'elementals') return sortModels(settings.elemental_models || []);
    return sortModels(settings.network_models || []);
  }, [activeTab, settings]);

  // Models for the form modal (based on the modal's category, not active tab)
  const modalModels = useMemo(() => {
    const cat = editingAsset ? editingCategory : addCategory;
    if (cat === 'servers') return sortModels(settings.server_models || []);
    if (cat === 'elementals') return sortModels(settings.elemental_models || []);
    return sortModels(settings.network_models || []);
  }, [editingAsset, editingCategory, addCategory, settings]);

  // Keyboard shortcuts (Ctrl+N = new asset, Ctrl+F = focus search)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
        e.preventDefault();
        setAddCategoryPickerVisible(true);
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        const active = document.activeElement;
        if (!active || (active.tagName !== 'INPUT' && active.tagName !== 'TEXTAREA')) {
          e.preventDefault();
          const searchEl = document.querySelector('[class*="filtering"] input') as HTMLInputElement;
          if (searchEl) searchEl.focus();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Handlers
  const handleTabChange = useCallback((tabId: string) => {
    setActiveTab(tabId as AssetCategory | 'images' | 'settings' | 'bulk_add');
    setCurrentPage(1);
    setSortingColumn(null);
    setSortingDescending(false);
    setSearchParams({ tab: tabId }, { replace: true });
  }, [setSearchParams]);

  const handleSortingChange = useCallback(({ detail }) => {
    setSortingColumn(detail.sortingColumn);
    setSortingDescending(detail.isDescending || false);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback(({ detail }) => {
    setCurrentPage(detail.currentPageIndex);
  }, []);

  // Filter change handlers - all reset pagination to page 1
  const handleSearchChange = useCallback(({ detail }) => {
    setSearchQuery(detail.filteringText);
    setCurrentPage(1);
  }, []);

  const handleSiteFilterChange = useCallback(({ detail }) => {
    const value = detail.selectedOption?.value || null;
    setSiteFilter(value === 'all' ? null : value);
    setCurrentPage(1);
  }, []);

  const handleWarrantyFilterChange = useCallback(({ detail }) => {
    const value = detail.selectedOption?.value || null;
    setWarrantyFilter(value === 'all' ? null : value);
    setCurrentPage(1);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearchQuery('');
    setSearchField(null);
    setSiteFilter(null);
    setStatusFilter(null);
    setWarrantyFilter(null);
    setMakeFilter(null);
    setModelFilter(null);
    setShowOnlyStorage(false);
    setCurrentPage(1);
  }, []);

  const handleAddAsset = useCallback(() => {
    setAddCategory(activeTab !== 'images' && activeTab !== 'settings' && activeTab !== 'bulk_add' ? activeTab : 'servers');
    setAddCategoryPickerVisible(true);
  }, [activeTab]);

  const handleAddCategoryConfirm = useCallback(() => {
    setAddCategoryPickerVisible(false);
    setEditingAsset(undefined);
    setModalVisible(true);
  }, []);

  const handleEditAsset = useCallback((asset: Asset, category?: AssetCategory) => {
    setDetailPanelVisible(false);
    setEditingAsset(asset);
    setEditingCategory(category || (activeTab !== 'images' && activeTab !== 'settings' && activeTab !== 'bulk_add' ? activeTab : 'servers'));
    setModalVisible(true);
  }, [activeTab]);

  const handleViewAsset = useCallback((asset: Asset, category?: AssetCategory) => {
    setSelectedAsset(asset);
    const cat = category || (activeTab !== 'images' && activeTab !== 'settings' && activeTab !== 'bulk_add' ? activeTab : 'servers');
    setEditingCategory(cat as AssetCategory);
    setDetailPanelVisible(true);
    setSearchParams({ tab: cat, asset: asset.id }, { replace: true });
  }, [activeTab, setSearchParams]);

  const handleDetailPanelDismiss = useCallback(() => {
    setDetailPanelVisible(false);
    setSelectedAsset(undefined);
    setSearchParams({ tab: activeTab }, { replace: true });
  }, [activeTab, setSearchParams]);

  const handleModalDismiss = useCallback(() => {
    setModalVisible(false);
    setEditingAsset(undefined);
  }, []);

  const handleModalSubmit = useCallback(
    async (assetData: Omit<Asset, 'id'> | Asset) => {
      if ('id' in assetData) {
        await updateAsset(editingCategory, assetData as Asset);
      } else {
        await addAsset(addCategory, assetData);
      }
      setModalVisible(false);
      setEditingAsset(undefined);
    },
    [editingCategory, addCategory, addAsset, updateAsset],
  );

  const handleExportBackup = useCallback(async () => {
    // Full workbook backup (all categories, every asset) generated by the
    // proxy from the .xlsx template. Independent of the current tab/filters.
    setExportError(null);
    setExporting(true);
    try {
      const res = await fetch('/inventory/export-xlsx');
      if (!res.ok) {
        let msg = `Export failed (HTTP ${res.status})`;
        try { const j = await res.json(); msg = j.detail || j.error || msg; } catch { /* non-JSON */ }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const today = new Date().toISOString().slice(0, 10);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `InfraSys Storage Inventory ${today}.xlsx`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportError(e?.message || 'Excel export failed. Is the local proxy running?');
    } finally {
      setExporting(false);
    }
  }, []);

  // Export all data for a specific category (used by Bulk Add tab)
  const handleExportAll = useCallback((category?: AssetCategory) => {
    const cat = category || (activeTab !== 'images' && activeTab !== 'settings' && activeTab !== 'bulk_add' ? activeTab as AssetCategory : 'servers');
    let data: Asset[];
    let columns: string[];
    switch (cat) {
      case 'servers':
        data = servers;
        columns = SERVER_COLUMNS;
        break;
      case 'elementals':
        data = elementals;
        columns = ELEMENTAL_COLUMNS;
        break;
      case 'network_devices':
        data = networkDevices;
        columns = NETWORK_DEVICE_COLUMNS;
        break;
      default:
        return;
    }
    if (data.length === 0) return;
    const filename = getExportFilename(CATEGORY_EXPORT_NAMES[cat]);
    exportToCSV(data as unknown as Record<string, unknown>[], columns, filename);
  }, [activeTab, servers, elementals, networkDevices]);

  // Bulk import handler. Await sequentially so each optimistic cache write sees
  // the previous one (concurrent un-awaited calls overwrite each other's cache).
  const handleBulkImport = useCallback(async (cat: AssetCategory, assets: Omit<Asset, 'id'>[]) => {
    for (const assetData of assets) {
      await addAsset(cat, assetData);
    }
    setBulkImportVisible(false);
  }, [addAsset]);

  // Bulk add handler (from UI tab)
  const handleBulkAdd = useCallback(async (cat: AssetCategory, assets: Omit<Asset, 'id'>[]) => {
    for (const assetData of assets) {
      await addAsset(cat, assetData);
    }
  }, [addAsset]);

  // Determine if any filters are active
  const hasActiveFilters = searchQuery.length > 0 || siteFilter !== null || statusFilter !== null || searchField !== null || showOnlyStorage || (warrantyFilter !== null && warrantyFilter !== 'all');

  // Build site dropdown options — from settings + "Other" for orphaned values
  const siteFilterOptions = useMemo(() => {
    // Check if any assets have site values not in settings
    const settingSites = new Set(settings.sites || []);
    const hasOrphans = [...servers, ...elementals, ...networkDevices].some(
      (asset) => asset.site && !settingSites.has(asset.site)
    );

    return [
      { label: 'All sites', value: 'all' },
      ...existingSites.map((site) => ({ label: site, value: site })),
      ...(hasOrphans ? [{ label: '⚠ Other / Unknown', value: '__other__' }] : []),
    ];
  }, [existingSites, settings.sites, servers, elementals, networkDevices]);

  // Counter text showing filtered vs total
  const counterText = useMemo(() => {
    if (hasActiveFilters) {
      return `(${sortedData.length} of ${currentData.length})`;
    }
    return `(${sortedData.length})`;
  }, [hasActiveFilters, sortedData.length, currentData.length]);

  // Server table column definitions — ALL fields available, visibility controlled by preferences
  const serverColumnDefinitions = [
    {
      id: 'actions',
      header: 'View/Edit',
      cell: (item: ServerAsset) => isMarkedForDeletion(item.id)
        ? <Button variant="inline-link" onClick={() => handleUndoDelete(item.id)}>Undo delete</Button>
        : <Button variant="inline-link" iconName="angle-right" onClick={() => handleViewAsset(item)}>View/Edit</Button>,
      width: 150,
    },
    { id: 'has_image', header: '📷', cell: (item: ServerAsset) => {
      const count = item.images?.length || 0;
      if (count > 0) return <span style={{ cursor: 'pointer', ...(isMarkedForDeletion(item.id) ? { opacity: 0.4, textDecoration: 'line-through' } : {}) }} onClick={() => handleViewAsset(item)}>📷 {count} <Button variant="inline-link" onClick={(e) => { e.stopPropagation(); handlePhotoColumnClick(item); }}>+ Add</Button></span>;
      return <span style={{ opacity: isMarkedForDeletion(item.id) ? 0.2 : 0.4 }}>📷 {count} <Button variant="inline-link" onClick={() => handlePhotoColumnClick(item)}>+ Add</Button></span>;
    }, width: 120 },
    { id: 'site', header: 'Site', cell: (item: ServerAsset) => <span style={isMarkedForDeletion(item.id) ? { textDecoration: 'line-through', opacity: 0.4 } : {}}>{item.site}</span>, sortingField: 'site', width: 160 },
    { id: 'hostname', header: 'Name', cell: (item: ServerAsset) => <span style={isMarkedForDeletion(item.id) ? { textDecoration: 'line-through', opacity: 0.4 } : {}}>{item.hostname}</span>, sortingField: 'hostname', width: 220 },
    { id: 'make', header: 'Make', cell: (item: ServerAsset) => item.make, sortingField: 'make', width: 100 },
    { id: 'model', header: 'Model', cell: (item: ServerAsset) => item.model, sortingField: 'model', width: 140 },
    { id: 'service_tag', header: 'Service Tag', cell: (item: ServerAsset) => (
      item.make?.toLowerCase() === 'dell'
        ? <a href={`https://www.dell.com/support/home/en-us/product-support/servicetag/${item.service_tag}/overview`} target="_blank" rel="noopener noreferrer" style={{ color: '#0073bb', textDecoration: 'none' }} title="View on Dell Support">{item.service_tag}</a>
        : item.service_tag
    ), sortingField: 'service_tag', width: 140 },
    { id: 'express_service_code', header: 'Express Code', cell: (item: ServerAsset) => item.express_service_code || '-', sortingField: 'express_service_code', width: 130 },
    { id: 'ship_date', header: 'Ship Date', cell: (item: ServerAsset) => formatDate(item.ship_date), sortingField: 'ship_date', width: 140 },
    {
      id: 'support_end_date',
      header: 'Warranty',
      cell: (item: ServerAsset) => (
        <SpaceBetween direction="horizontal" size="xs">
          <span>{formatDate(item.support_end_date)}</span>
          {renderWarrantyIndicator(item.support_end_date)}
        </SpaceBetween>
      ),
      sortingField: 'support_end_date',
      width: 180,
    },
    { id: 'status', header: 'Status', cell: (item: ServerAsset) => renderStatusBadge(item.status), sortingField: 'status', width: 160 },
    { id: 'cpu_model', header: 'CPU Model', cell: (item: ServerAsset) => displayOptionalField(item.cpu_model), sortingField: 'cpu_model', width: 240 },
    { id: 'cpu_quantity', header: 'CPU Qty', cell: (item: ServerAsset) => displayOptionalField(item.cpu_quantity), sortingField: 'cpu_quantity', width: 100 },
    { id: 'ram_model', header: 'RAM Model', cell: (item: ServerAsset) => displayOptionalField(item.ram_model), sortingField: 'ram_model', width: 240 },
    { id: 'ram_quantity', header: 'RAM Qty', cell: (item: ServerAsset) => displayOptionalField(item.ram_quantity), sortingField: 'ram_quantity', width: 100 },
    { id: 'disk_model', header: 'Disk Model', cell: (item: ServerAsset) => displayOptionalField(item.disk_model), sortingField: 'disk_model', width: 260 },
    { id: 'disk_quantity', header: 'Disk Qty', cell: (item: ServerAsset) => displayOptionalField(item.disk_quantity), sortingField: 'disk_quantity', width: 100 },
    { id: 'last_known_contact', header: 'Last Known Contact', cell: (item: ServerAsset) => displayOptionalField(item.last_known_contact), sortingField: 'last_known_contact', width: 160 },
    { id: 'purchase_order', header: 'PO #', cell: (item: ServerAsset) => displayOptionalField(item.purchase_order), sortingField: 'purchase_order', width: 140 },
    { id: 'cost', header: 'Cost', cell: (item: ServerAsset) => item.cost ? `$${item.cost.toLocaleString()}` : '-', sortingField: 'cost', width: 120 },
    { id: 'location_detail', header: 'Location', cell: (item: ServerAsset) => displayOptionalField(item.location_detail), sortingField: 'location_detail', width: 200 },
    { id: 'notes', header: 'Notes', cell: (item: ServerAsset) => displayOptionalField(item.notes), sortingField: 'notes', width: 250 },
  ];

  // Elemental table column definitions — ALL fields available
  const elementalColumnDefinitions = [
    {
      id: 'actions',
      header: 'View/Edit',
      cell: (item: ElementalAsset) => isMarkedForDeletion(item.id)
        ? <Button variant="inline-link" onClick={() => handleUndoDelete(item.id)}>Undo delete</Button>
        : <Button variant="inline-link" iconName="angle-right" onClick={() => handleViewAsset(item)}>View/Edit</Button>,
      width: 150,
    },
    { id: 'has_image', header: '📷', cell: (item: ElementalAsset) => {
      const count = item.images?.length || 0;
      if (count > 0) return <span style={{ cursor: 'pointer', ...(isMarkedForDeletion(item.id) ? { opacity: 0.4, textDecoration: 'line-through' } : {}) }} onClick={() => handleViewAsset(item)}>📷 {count} <Button variant="inline-link" onClick={(e) => { e.stopPropagation(); handlePhotoColumnClick(item); }}>+ Add</Button></span>;
      return <span style={{ opacity: isMarkedForDeletion(item.id) ? 0.2 : 0.4 }}>📷 {count} <Button variant="inline-link" onClick={() => handlePhotoColumnClick(item)}>+ Add</Button></span>;
    }, width: 120 },
    { id: 'site', header: 'Site', cell: (item: ElementalAsset) => <span style={isMarkedForDeletion(item.id) ? { textDecoration: 'line-through', opacity: 0.4 } : {}}>{item.site}</span>, sortingField: 'site', width: 160 },
    { id: 'series', header: 'Series', cell: (item: ElementalAsset) => item.series, sortingField: 'series', width: 100 },
    { id: 'manufacturer', header: 'Make', cell: (item: ElementalAsset) => item.manufacturer, sortingField: 'manufacturer', width: 100 },
    { id: 'model', header: 'Model', cell: (item: ElementalAsset) => item.model, sortingField: 'model', width: 140 },
    { id: 'service_tag', header: 'Service Tag', cell: (item: ElementalAsset) => (
      item.manufacturer?.toLowerCase() === 'dell' || item.manufacturer?.toLowerCase() === 'aws'
        ? <a href={`https://www.dell.com/support/home/en-us/product-support/servicetag/${item.service_tag}/overview`} target="_blank" rel="noopener noreferrer" style={{ color: '#0073bb', textDecoration: 'none' }} title="View on Dell Support">{item.service_tag}</a>
        : item.service_tag
    ), sortingField: 'service_tag', width: 140 },
    { id: 'express_service_code', header: 'Express Code', cell: (item: ElementalAsset) => item.express_service_code || '-', sortingField: 'express_service_code', width: 130 },
    { id: 'boxed_status', header: 'Boxed', cell: (item: ElementalAsset) => item.boxed_status, sortingField: 'boxed_status', width: 110 },
    { id: 'condition', header: 'Condition', cell: (item: ElementalAsset) => item.condition, sortingField: 'condition', width: 120 },
    { id: 'status', header: 'Status', cell: (item: ElementalAsset) => renderStatusBadge(item.status), sortingField: 'status', width: 160 },
    { id: 'location', header: 'Location', cell: (item: ElementalAsset) => displayOptionalField(item.location), sortingField: 'location', width: 180 },
    { id: 'notes', header: 'Notes', cell: (item: ElementalAsset) => displayOptionalField(item.notes), sortingField: 'notes', width: 250 },
    {
      id: 'to_be_decommissioned',
      header: 'Decomm',
      cell: (item: ElementalAsset) =>
        item.to_be_decommissioned ? (
          <StatusIndicator type="warning">Yes</StatusIndicator>
        ) : (
          <span>-</span>
        ),
      sortingField: 'to_be_decommissioned',
      width: 110,
    },
    { id: 'last_known_contact', header: 'Last Known Contact', cell: (item: ElementalAsset) => displayOptionalField(item.last_known_contact), sortingField: 'last_known_contact', width: 160 },
    { id: 'purchase_order', header: 'PO #', cell: (item: ElementalAsset) => displayOptionalField(item.purchase_order), sortingField: 'purchase_order', width: 140 },
    { id: 'cost', header: 'Cost', cell: (item: ElementalAsset) => item.cost ? `$${item.cost.toLocaleString()}` : '-', sortingField: 'cost', width: 120 },
    { id: 'location_detail', header: 'Location', cell: (item: ElementalAsset) => displayOptionalField(item.location_detail), sortingField: 'location_detail', width: 200 },
  ];

  // Network device table column definitions — ALL fields available
  const networkDeviceColumnDefinitions = [
    {
      id: 'actions',
      header: 'View/Edit',
      cell: (item: NetworkDeviceAsset) => isMarkedForDeletion(item.id)
        ? <Button variant="inline-link" onClick={() => handleUndoDelete(item.id)}>Undo delete</Button>
        : <Button variant="inline-link" iconName="angle-right" onClick={() => handleViewAsset(item)}>View/Edit</Button>,
      width: 150,
    },
    { id: 'has_image', header: '📷', cell: (item: NetworkDeviceAsset) => {
      const count = item.images?.length || 0;
      if (count > 0) return <span style={{ cursor: 'pointer', ...(isMarkedForDeletion(item.id) ? { opacity: 0.4, textDecoration: 'line-through' } : {}) }} onClick={() => handleViewAsset(item)}>📷 {count} <Button variant="inline-link" onClick={(e) => { e.stopPropagation(); handlePhotoColumnClick(item); }}>+ Add</Button></span>;
      return <span style={{ opacity: isMarkedForDeletion(item.id) ? 0.2 : 0.4 }}>📷 {count} <Button variant="inline-link" onClick={() => handlePhotoColumnClick(item)}>+ Add</Button></span>;
    }, width: 120 },
    { id: 'site', header: 'Site', cell: (item: NetworkDeviceAsset) => <span style={isMarkedForDeletion(item.id) ? { textDecoration: 'line-through', opacity: 0.4 } : {}}>{item.site}</span>, sortingField: 'site', width: 160 },
    { id: 'hostname', header: 'Name', cell: (item: NetworkDeviceAsset) => <span style={isMarkedForDeletion(item.id) ? { textDecoration: 'line-through', opacity: 0.4 } : {}}>{displayOptionalField(item.hostname)}</span>, sortingField: 'hostname', width: 200 },
    { id: 'make', header: 'Make', cell: (item: NetworkDeviceAsset) => item.make, sortingField: 'make', width: 120 },
    { id: 'model', header: 'Model', cell: (item: NetworkDeviceAsset) => item.model, sortingField: 'model', width: 220 },
    { id: 'serial_num', header: 'Serial Number', cell: (item: NetworkDeviceAsset) => item.serial_num, sortingField: 'serial_num', width: 160 },
    {
      id: 'support_end_date',
      header: 'Warranty',
      cell: (item: NetworkDeviceAsset) => (
        <SpaceBetween direction="horizontal" size="xs">
          <span>{formatDate(item.support_end_date)}</span>
          {renderWarrantyIndicator(item.support_end_date)}
        </SpaceBetween>
      ),
      sortingField: 'support_end_date',
      width: 180,
    },
    { id: 'status', header: 'Status', cell: (item: NetworkDeviceAsset) => renderStatusBadge(item.status), sortingField: 'status', width: 160 },
    { id: 'customer_order', header: 'Order #', cell: (item: NetworkDeviceAsset) => displayOptionalField(item.customer_order), sortingField: 'customer_order', width: 170 },
    { id: 'shipping_box', header: 'Box #', cell: (item: NetworkDeviceAsset) => displayOptionalField(item.shipping_box), sortingField: 'shipping_box', width: 150 },
    { id: 'tracking_num', header: 'Tracking #', cell: (item: NetworkDeviceAsset) => displayOptionalField(item.tracking_num), sortingField: 'tracking_num', width: 170 },
    { id: 'quantity', header: 'Qty', cell: (item: NetworkDeviceAsset) => item.quantity, sortingField: 'quantity', width: 80 },
    { id: 'last_known_contact', header: 'Last Known Contact', cell: (item: NetworkDeviceAsset) => displayOptionalField(item.last_known_contact), sortingField: 'last_known_contact', width: 160 },
    { id: 'purchase_order', header: 'PO #', cell: (item: NetworkDeviceAsset) => displayOptionalField(item.purchase_order), sortingField: 'purchase_order', width: 140 },
    { id: 'cost', header: 'Cost', cell: (item: NetworkDeviceAsset) => item.cost ? `$${item.cost.toLocaleString()}` : '-', sortingField: 'cost', width: 120 },
    { id: 'location_detail', header: 'Location', cell: (item: NetworkDeviceAsset) => displayOptionalField(item.location_detail), sortingField: 'location_detail', width: 200 },
    { id: 'notes', header: 'Notes', cell: (item: NetworkDeviceAsset) => displayOptionalField(item.notes), sortingField: 'notes', width: 250 },
  ];

  // Get column definitions for active tab
  const getColumnDefinitions = () => {
    let allColumns: { id: string }[];
    switch (activeTab) {
      case 'servers':
        allColumns = serverColumnDefinitions;
        break;
      case 'elementals':
        allColumns = elementalColumnDefinitions;
        break;
      case 'network_devices':
        allColumns = networkDeviceColumnDefinitions;
        break;
      default:
        return [];
    }
    // Filter to only visible columns. Notes is force-included on every tab so it
    // always shows regardless of saved column preferences.
    const visible = visibleColumns[activeTab as AssetCategory] || [];
    const effectiveVisible = visible.includes('notes') ? visible : [...visible, 'notes'];
    return allColumns.filter((col) => effectiveVisible.includes(col.id));
  };

  // Content display options for CollectionPreferences
  const getContentDisplayOptions = () => {
    switch (activeTab) {
      case 'servers':
        return [
          { id: 'site', label: 'Site' },
          { id: 'hostname', label: 'Hostname' },
          { id: 'make', label: 'Make' },
          { id: 'model', label: 'Model' },
          { id: 'service_tag', label: 'Service Tag' },
          { id: 'express_service_code', label: 'Express Service Code' },
          { id: 'ship_date', label: 'Ship Date' },
          { id: 'support_end_date', label: 'Warranty' },
          { id: 'status', label: 'Status' },
          { id: 'cpu_model', label: 'CPU Model' },
          { id: 'cpu_quantity', label: 'CPU Qty' },
          { id: 'ram_model', label: 'RAM Model' },
          { id: 'ram_quantity', label: 'RAM Qty' },
          { id: 'disk_model', label: 'Disk Model' },
          { id: 'disk_quantity', label: 'Disk Qty' },
          { id: 'last_known_contact', label: 'Last Known Contact' },
          { id: 'purchase_order', label: 'Purchase Order' },
          { id: 'cost', label: 'Cost' },
          { id: 'location_detail', label: 'Location Detail' },
          { id: 'notes', label: 'Notes' },
        ];
      case 'elementals':
        return [
          { id: 'site', label: 'Site' },
          { id: 'series', label: 'Series' },
          { id: 'manufacturer', label: 'Manufacturer' },
          { id: 'model', label: 'Model' },
          { id: 'service_tag', label: 'Service Tag' },
          { id: 'express_service_code', label: 'Express Service Code' },
          { id: 'boxed_status', label: 'Boxed/Unboxed' },
          { id: 'condition', label: 'Condition' },
          { id: 'status', label: 'Status' },
          { id: 'location', label: 'Location' },
          { id: 'notes', label: 'Notes' },
          { id: 'to_be_decommissioned', label: 'Decommission' },
          { id: 'last_known_contact', label: 'Last Known Contact' },
          { id: 'purchase_order', label: 'Purchase Order' },
          { id: 'cost', label: 'Cost' },
          { id: 'location_detail', label: 'Location Detail' },
        ];
      case 'network_devices':
        return [
          { id: 'site', label: 'Site' },
          { id: 'hostname', label: 'Hostname' },
          { id: 'make', label: 'Make' },
          { id: 'model', label: 'Model' },
          { id: 'serial_num', label: 'Serial #' },
          { id: 'support_end_date', label: 'Warranty' },
          { id: 'status', label: 'Status' },
          { id: 'customer_order', label: 'Customer Order' },
          { id: 'shipping_box', label: 'Shipping Box' },
          { id: 'tracking_num', label: 'Tracking #' },
          { id: 'quantity', label: 'Quantity' },
          { id: 'last_known_contact', label: 'Last Known Contact' },
          { id: 'purchase_order', label: 'Purchase Order' },
          { id: 'cost', label: 'Cost' },
          { id: 'location_detail', label: 'Location Detail' },
          { id: 'notes', label: 'Notes' },
        ];
      default:
        return [];
    }
  };

  // Collect all assets with images for the Images tab
  const allAssetsWithImages = useMemo(() => {
    const items: { asset: Asset; category: AssetCategory; imageIndex: number; image: string }[] = [];
    const addFromCategory = (assets: Asset[], cat: AssetCategory) => {
      for (const asset of assets) {
        if (asset.images && asset.images.length > 0) {
          asset.images.forEach((img, idx) => {
            items.push({ asset, category: cat, imageIndex: idx, image: img });
          });
        }
      }
    };
    addFromCategory(servers, 'servers');
    addFromCategory(elementals, 'elementals');
    addFromCategory(networkDevices, 'network_devices');
    return items;
  }, [servers, elementals, networkDevices]);

  // Filtered version that respects active filters (site, status, search, etc.)
  const filteredAssetsWithImages = useMemo(() => {
    if (!hasActiveFilters) return allAssetsWithImages;

    // Apply filters to each category's assets, then collect images from results
    const items: { asset: Asset; category: AssetCategory; imageIndex: number; image: string }[] = [];
    const filterCategory = (assets: Asset[], cat: AssetCategory) => {
      let result = applyAllFilters(assets, {
        textQuery: searchQuery,
        searchableFields: (SEARCHABLE_FIELDS[cat] || []) as unknown as (keyof Asset)[],
        site: siteFilter === '__other__' ? null : siteFilter,
        status: statusFilter,
      });

      // "Other / Unknown" filter: show only assets whose site is NOT in settings
      if (siteFilter === '__other__') {
        const settingSites = new Set(settings.sites || []);
        result = result.filter((asset) => !settingSites.has(asset.site));
      }

      if (makeFilter) {
        result = result.filter((asset) => {
          if ('make' in asset) return asset.make === makeFilter;
          if ('manufacturer' in asset) return asset.manufacturer === makeFilter;
          return false;
        });
      }
      if (modelFilter) {
        result = result.filter((asset) => 'model' in asset && asset.model === modelFilter);
      }
      if (warrantyFilter && warrantyFilter !== 'all') {
        result = filterByWarranty(result, warrantyFilter as 'expired' | '30' | '60' | '90');
      }

      for (const asset of result) {
        if (asset.images && asset.images.length > 0) {
          asset.images.forEach((img, idx) => {
            items.push({ asset, category: cat, imageIndex: idx, image: img });
          });
        }
      }
    };
    filterCategory(servers, 'servers');
    filterCategory(elementals, 'elementals');
    filterCategory(networkDevices, 'network_devices');
    return items;
  }, [allAssetsWithImages, servers, elementals, networkDevices, hasActiveFilters, searchQuery, siteFilter, statusFilter, makeFilter, modelFilter, warrantyFilter, settings.sites]);

  // Toggle between showing all photos or only filtered
  const [showAllPhotos, setShowAllPhotos] = useState(false);
  const assetsWithImages = showAllPhotos ? allAssetsWithImages : filteredAssetsWithImages;

  // Pending deletions — assets marked for deletion with 5-minute undo window
  // Persisted to localStorage so it survives page reload
  const [pendingDeletions, setPendingDeletions] = useState<Map<string, { asset: Asset; category: AssetCategory; deleteAt: number }>>(() => {
    try {
      const stored = localStorage.getItem('storage-inventory-pending-deletions');
      if (stored) {
        const entries = JSON.parse(stored) as { id: string; asset: Asset; category: AssetCategory; deleteAt: number }[];
        const now = Date.now();
        // Filter out any that have already expired
        const valid = entries.filter((e) => e.deleteAt > now);
        return new Map(valid.map((e) => [e.id, { asset: e.asset, category: e.category, deleteAt: e.deleteAt }]));
      }
    } catch { /* ignore corrupt pending-deletions cache */ }
    return new Map();
  });

  // Persist pending deletions to localStorage whenever they change
  useEffect(() => {
    const entries = Array.from(pendingDeletions.entries()).map(([id, entry]) => ({
      id,
      asset: entry.asset,
      category: entry.category,
      deleteAt: entry.deleteAt,
    }));
    localStorage.setItem('storage-inventory-pending-deletions', JSON.stringify(entries));
  }, [pendingDeletions]);

  // Timer to permanently delete expired items
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      let changed = false;
      pendingDeletions.forEach((entry, id) => {
        if (entry.deleteAt <= now) {
          deleteAsset(entry.category, id);
          changed = true;
        }
      });
      if (changed) {
        setPendingDeletions((prev) => {
          const next = new Map(prev);
          const now2 = Date.now();
          next.forEach((entry, id) => {
            if (entry.deleteAt <= now2) next.delete(id);
          });
          return next;
        });
      }
    }, 10000); // Check every 10 seconds
    return () => clearInterval(interval);
  }, [pendingDeletions, deleteAsset]);

  const handleDeleteAsset = useCallback((asset: Asset, category?: AssetCategory) => {
    const cat = category || (activeTab !== 'images' && activeTab !== 'settings' && activeTab !== 'bulk_add' ? activeTab : 'servers') as AssetCategory;
    const deleteAt = Date.now() + 5 * 60 * 1000; // 5 minutes from now
    setPendingDeletions((prev) => {
      const next = new Map(prev);
      next.set(asset.id, { asset, category: cat, deleteAt });
      return next;
    });
  }, [activeTab]);

  const handleUndoDelete = useCallback((assetId: string) => {
    setPendingDeletions((prev) => {
      const next = new Map(prev);
      next.delete(assetId);
      return next;
    });
  }, []);

  const isMarkedForDeletion = useCallback((assetId: string) => {
    return pendingDeletions.has(assetId);
  }, [pendingDeletions]);

  const getAssetLabel = (asset: Asset, category: AssetCategory): string => {
    if (category === 'servers') {
      const s = asset as ServerAsset;
      return s.hostname || s.service_tag;
    }
    if (category === 'elementals') {
      const e = asset as ElementalAsset;
      return `${e.manufacturer} ${e.model} (${e.service_tag})`;
    }
    const n = asset as NetworkDeviceAsset;
    return n.hostname || `${n.make} ${n.model}`;
  };

  const getCategoryLabel = (cat: AssetCategory): string => {
    switch (cat) {
      case 'servers': return 'Server';
      case 'elementals': return 'Elemental';
      case 'network_devices': return 'Network Device';
    }
  };

  // Lightbox state for expanded image view
  const [lightboxImage, setLightboxImage] = useState<{ image: string; asset: Asset; category: AssetCategory; imageIndex: number } | null>(null);

  const renderImagesGallery = () => {
    if (allAssetsWithImages.length === 0) {
      return (
        <Box textAlign="center" color="inherit" padding="xxl">
          <b>No photos</b>
          <Box padding={{ bottom: 's' }} variant="p" color="inherit">
            No assets have photos attached yet. Use the Details panel to add photos to any asset.
          </Box>
        </Box>
      );
    }

    return (
      <SpaceBetween size="l">
        <SpaceBetween direction="horizontal" size="m">
          <Box variant="p" color="text-body-secondary">
            {showAllPhotos
              ? `Showing all ${allAssetsWithImages.length} photo${allAssetsWithImages.length !== 1 ? 's' : ''}`
              : hasActiveFilters
                ? `Showing ${assetsWithImages.length} of ${allAssetsWithImages.length} photos (filtered)`
                : `${assetsWithImages.length} photo${assetsWithImages.length !== 1 ? 's' : ''}`
            }
            {' '}across {new Set(assetsWithImages.map(i => i.asset.id)).size} asset{new Set(assetsWithImages.map(i => i.asset.id)).size !== 1 ? 's' : ''}
          </Box>
          {hasActiveFilters && (
            <Button
              variant={showAllPhotos ? 'normal' : 'primary'}
              onClick={() => setShowAllPhotos(!showAllPhotos)}
            >
              {showAllPhotos ? 'Apply filters' : 'Show all photos'}
            </Button>
          )}
        </SpaceBetween>

        {assetsWithImages.length === 0 && hasActiveFilters && !showAllPhotos ? (
          <Box textAlign="center" color="inherit" padding="l">
            <b>No photos match current filters</b>
            <Box padding={{ bottom: 's' }} variant="p" color="inherit">
              Try adjusting filters or click "Show all photos" to see everything.
            </Box>
          </Box>
        ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
          {assetsWithImages.map((item, idx) => (
            <div
              key={`${item.asset.id}-${item.imageIndex}-${idx}`}
              style={{
                border: '1px solid #d1d5db',
                borderRadius: 8,
                overflow: 'hidden',
                background: '#fafafa',
              }}
            >
              <div
                style={{ cursor: 'zoom-in', position: 'relative' }}
                onClick={() => setLightboxImage(item)}
              >
                <PhotoImage
                  photo={item.image}
                  alt={`${getAssetLabel(item.asset, item.category)} photo ${item.imageIndex + 1}`}
                  style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }}
                />
                <div style={{
                  position: 'absolute', bottom: 8, right: 8,
                  background: 'rgba(0,0,0,0.6)', color: 'white',
                  borderRadius: 4, padding: '2px 8px', fontSize: 12,
                }}>
                  Click to expand
                </div>
              </div>
              <div style={{ padding: '12px' }}>
                <Box variant="awsui-key-label" fontSize="body-s">{getCategoryLabel(item.category)}</Box>
                <div style={{ marginTop: 4 }}>
                  <Button
                    variant="inline-link"
                    iconName="external"
                    iconAlign="right"
                    onClick={() => handleViewAsset(item.asset, item.category)}
                  >
                    {getAssetLabel(item.asset, item.category)}
                  </Button>
                </div>
                <Box variant="small" color="text-body-secondary" margin={{ top: 'xxs' }}>
                  {item.asset.site || ''} • Photo {item.imageIndex + 1}
                </Box>
              </div>
            </div>
          ))}
        </div>
        )}

        {/* Lightbox overlay */}
        {lightboxImage && (
          <div
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.85)', zIndex: 9999,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              cursor: 'zoom-out',
            }}
            onClick={() => setLightboxImage(null)}
          >
            <div style={{ position: 'absolute', top: 16, right: 16 }}>
              <button
                onClick={() => setLightboxImage(null)}
                style={{
                  background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
                  fontSize: 24, width: 40, height: 40, borderRadius: '50%', cursor: 'pointer',
                }}
              >
                ✕
              </button>
            </div>
            <PhotoImage
              photo={lightboxImage.image}
              alt="Expanded view"
              style={{
                maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain',
                borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
              }}
              onClick={(e) => e.stopPropagation()}
            />
            <div style={{ marginTop: 16, color: 'white', textAlign: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 600 }}>
                {getAssetLabel(lightboxImage.asset, lightboxImage.category)}
              </div>
              <div style={{ fontSize: 13, opacity: 0.8, marginTop: 4 }}>
                {lightboxImage.asset.site} • {getCategoryLabel(lightboxImage.category)} • Photo {lightboxImage.imageIndex + 1}
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxImage(null);
                  handleViewAsset(lightboxImage.asset, lightboxImage.category);
                }}
                style={{
                  marginTop: 12, background: '#0073bb', color: 'white', border: 'none',
                  padding: '8px 16px', borderRadius: 4, cursor: 'pointer', fontSize: 14,
                }}
              >
                View device details →
              </button>
            </div>
          </div>
        )}
      </SpaceBetween>
    );
  };

  const emptyStateMessage = () => (
    <Box textAlign="center" color="inherit">
      <b>No assets</b>
      <Box padding={{ bottom: 's' }} variant="p" color="inherit">
        No assets are available in this category.
      </Box>
    </Box>
  );

  const renderTable = () => (
    <SpaceBetween size="s">
      {/* Pending deletion undo banners */}
      {Array.from(pendingDeletions.values())
        .filter((entry) => entry.category === (activeTab !== 'images' && activeTab !== 'settings' && activeTab !== 'bulk_add' ? activeTab : ''))
        .map((entry) => (
          <Alert key={entry.asset.id} type="warning" dismissible onDismiss={() => handleUndoDelete(entry.asset.id)}
            action={<Button onClick={() => handleUndoDelete(entry.asset.id)}>Undo</Button>}
          >
            <span style={{ textDecoration: 'line-through' }}>
              {'hostname' in entry.asset ? entry.asset.hostname : ('service_tag' in entry.asset ? entry.asset.service_tag : entry.asset.id)}
            </span>
            {' '}marked for deletion. Will be permanently removed in ~{Math.max(1, Math.ceil((entry.deleteAt - Date.now()) / 60000))} min.
          </Alert>
        ))
      }
      <Table
        columnDefinitions={getColumnDefinitions() as unknown as TableProps.ColumnDefinition<Asset>[]}
        items={paginatedData}
        loading={isLoading}
        loadingText="Loading assets..."
        empty={emptyStateMessage()}
        sortingColumn={sortingColumn}
        sortingDescending={sortingDescending}
        onSortingChange={handleSortingChange}
        pagination={
          <Pagination
            currentPageIndex={currentPage}
            pagesCount={totalPages}
            onChange={handlePageChange}
          />
        }
        preferences={
          <CollectionPreferences
            title="Preferences"
            confirmLabel="Confirm"
            cancelLabel="Cancel"
            preferences={{
              pageSize,
              contentDisplay: (visibleColumns[activeTab as AssetCategory] || [])
                .filter((id) => id !== 'actions')
                .map((id) => ({ id, visible: true })),
            }}
            pageSizePreference={{
              title: 'Page size',
              options: [...PAGE_SIZE_OPTIONS.map((size) => ({
                value: size,
                label: `${size} items`,
              })), { value: 9999, label: 'All' }],
            }}
            contentDisplayPreference={{
              title: 'Column preferences',
              description: 'Select which columns to display',
              options: getContentDisplayOptions(),
            }}
            onConfirm={({ detail }) => {
            if (detail.pageSize) {
              setPageSize(detail.pageSize);
              setCurrentPage(1);
            }
            if (detail.contentDisplay && activeTab !== 'images') {
              const newVisible = ['actions', ...detail.contentDisplay.filter((c) => c.visible).map((c) => c.id)];
              // Notes is always shown on every tab, so keep it selected.
              if (!newVisible.includes('notes')) newVisible.push('notes');
              setVisibleColumns((prev) => ({ ...prev, [activeTab]: newVisible }));
            }
          }}
        />
      }
      trackBy="id"
      variant="container"
      resizableColumns
      stickyHeader
      stripedRows
    />
    </SpaceBetween>
  );

  // Loading / error states. These early returns MUST come after every hook
  // above so hooks always run in the same order on every render (React's
  // rules-of-hooks); rendering the table below is skipped while loading/erroring.
  if (isLoading) {
    return (
      <ContentLayout header={<Header variant="h1">Storage Inventory</Header>}>
        <Box textAlign="center" padding="xxl">
          <Spinner size="large" />
          <Box variant="p" padding={{ top: 's' }}>
            Loading inventory data...
          </Box>
        </Box>
      </ContentLayout>
    );
  }

  if (error) {
    return (
      <ContentLayout header={<Header variant="h1">Storage Inventory</Header>}>
        <StatusIndicator type="error">
          Failed to load inventory data: {error.message}
        </StatusIndicator>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description="Track unracked and boxed inventory across datacenters"
          counter={counterText}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="primary" iconName="add-plus" onClick={handleAddAsset}>
                Add asset <span style={{ opacity: 0.7, fontSize: 11 }}>(Ctrl+N)</span>
              </Button>
              <Button
                iconName="download"
                onClick={handleExportBackup}
                loading={exporting}
                disabled={servers.length + elementals.length + networkDevices.length === 0}
              >
                Export backup (.xlsx)
              </Button>
            </SpaceBetween>
          }
        >
          Storage Inventory
        </Header>
      }
    >
      <SpaceBetween size="l">
        {exportError && (
          <Alert type="error" dismissible onDismiss={() => setExportError(null)} header="Backup export failed">
            {exportError}
          </Alert>
        )}
        {/* Summary stats — works in light and dark mode */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr 1fr 1fr 0.8fr 1fr', gap: '1px', background: 'var(--color-border-divider-default, #e9ebed)', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--color-border-divider-default, #e9ebed)' }}>
          <div style={{ background: 'var(--color-background-container-content, #ffffff)', padding: '16px 12px', textAlign: 'center' }}>
            <div style={{ color: 'var(--color-text-body-secondary, #5f6b7a)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Total Assets</div>
            <div style={{ color: 'var(--color-text-heading-default, #000716)', fontSize: 28, fontWeight: 700 }}>{[...servers, ...elementals, ...networkDevices].filter((a) => !isMarkedForDeletion(a.id)).length}</div>
          </div>
          <div style={{ background: 'var(--color-background-container-content, #ffffff)', padding: '16px 12px', textAlign: 'center' }}>
            <div style={{ color: 'var(--color-text-body-secondary, #5f6b7a)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4, whiteSpace: 'nowrap' }}>Servers / Encoders / Appliances</div>
            <div style={{ color: '#0073bb', fontSize: 28, fontWeight: 700 }}>{servers.filter((a) => !isMarkedForDeletion(a.id)).length}</div>
          </div>
          <div style={{ background: 'var(--color-background-container-content, #ffffff)', padding: '16px 12px', textAlign: 'center' }}>
            <div style={{ color: 'var(--color-text-body-secondary, #5f6b7a)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Spare Elementals</div>
            <div style={{ color: '#0073bb', fontSize: 28, fontWeight: 700 }}>{elementals.filter((a) => !isMarkedForDeletion(a.id)).length}</div>
          </div>
          <div style={{ background: 'var(--color-background-container-content, #ffffff)', padding: '16px 12px', textAlign: 'center' }}>
            <div style={{ color: 'var(--color-text-body-secondary, #5f6b7a)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Network Devices</div>
            <div style={{ color: '#0073bb', fontSize: 28, fontWeight: 700 }}>{networkDevices.filter((a) => !isMarkedForDeletion(a.id)).length}</div>
          </div>
          <div style={{ background: 'var(--color-background-container-content, #ffffff)', padding: '16px 12px', textAlign: 'center', cursor: 'pointer', transition: 'background 0.15s' }} onClick={() => { setStatusFilter(statusFilter === 'has issues' ? null : 'has issues'); setCurrentPage(1); }} title="Click to filter by 'has issues'">
            <div style={{ color: 'var(--color-text-body-secondary, #5f6b7a)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Has Issues {statusFilter === 'has issues' ? '✕' : ''}</div>
            <div style={{ color: [...servers, ...networkDevices].filter((a) => a.status === 'has issues' && !isMarkedForDeletion(a.id)).length > 0 ? '#d13212' : '#037f0c', fontSize: 28, fontWeight: 700 }}>
              {[...servers, ...networkDevices].filter((a) => a.status === 'has issues' && !isMarkedForDeletion(a.id)).length}
            </div>
          </div>
          <div style={{ background: 'var(--color-background-container-content, #ffffff)', padding: '16px 12px', textAlign: 'center', cursor: 'pointer', transition: 'background 0.15s' }} onClick={() => { setWarrantyFilter(warrantyFilter === 'expired' ? null : 'expired'); setCurrentPage(1); }} title="Click to filter by expired warranty">
            <div style={{ color: 'var(--color-text-body-secondary, #5f6b7a)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Warranty Expired {warrantyFilter === 'expired' ? '✕' : ''}</div>
            <div style={{ color: [...servers, ...networkDevices].filter((a) => { if (isMarkedForDeletion(a.id)) return false; const d = a.support_end_date; if (!d) return false; return new Date(d) < new Date(); }).length > 0 ? '#ce5a00' : '#037f0c', fontSize: 28, fontWeight: 700 }}>
              {[...servers, ...networkDevices].filter((a) => { if (isMarkedForDeletion(a.id)) return false; const d = a.support_end_date; if (!d) return false; return new Date(d) < new Date(); }).length}
            </div>
          </div>
        </div>


        <Container>
          <SpaceBetween size="m">
            <SpaceBetween direction="horizontal" size="m">
              <div style={{ minWidth: '160px' }}>
                <FormField label="Site">
                  <Select
                    selectedOption={
                      siteFilter
                        ? { label: siteFilter === '__other__' ? '⚠ Other / Unknown' : siteFilter, value: siteFilter }
                        : { label: 'All sites', value: 'all' }
                    }
                    onChange={handleSiteFilterChange}
                    options={siteFilterOptions}
                    placeholder="All sites"
                  />
                </FormField>
              </div>
              <div style={{ minWidth: '160px' }}>
                <FormField label="Search by">
                  <Select
                    selectedOption={searchField ? { label: searchField === 'service_tag' ? 'Service Tag / Serial #' : searchField.charAt(0).toUpperCase() + searchField.slice(1), value: searchField } : { label: 'All fields', value: 'all' }}
                    onChange={({ detail }) => {
                      const val = detail.selectedOption.value === 'all' ? null : detail.selectedOption.value!;
                      setSearchField(val);
                      setSearchQuery('');
                      setCurrentPage(1);
                    }}
                    options={[
                      { label: 'All fields', value: 'all' },
                      { label: 'Hostname', value: 'hostname' },
                      { label: 'Make', value: 'make' },
                      { label: 'Model', value: 'model' },
                      { label: 'Service Tag / Serial #', value: 'service_tag' },
                      { label: 'Status', value: 'status' },
                      { label: 'Notes', value: 'notes' },
                    ]}
                    placeholder="All fields"
                  />
                </FormField>
              </div>
              <div style={{ minWidth: '300px', flex: 1 }}>
                <FormField label={searchField ? `Search: ${searchField === 'service_tag' ? 'Service Tag / Serial #' : searchField}` : 'Search'}>
                  {searchField === 'status' ? (
                    <Select
                      selectedOption={searchQuery ? { label: searchQuery, value: searchQuery } : null}
                      onChange={({ detail }) => {
                        const value = detail.selectedOption.value || '';
                        setSearchQuery(value);
                        // The "In Storage" toggle hard-filters to status === 'in storage',
                        // which conflicts with picking a different status. Uncheck it so
                        // the selected status actually shows results.
                        if (value && value !== 'in storage') {
                          setShowOnlyStorage(false);
                        }
                        setCurrentPage(1);
                      }}
                      options={(settings.statuses && settings.statuses.length > 0 ? settings.statuses : DEFAULT_ASSET_STATUSES).map((s) => ({ label: s, value: s }))}
                      placeholder="Select status..."
                    />
                  ) : searchField === 'make' ? (
                    <Select
                      selectedOption={searchQuery ? { label: searchQuery, value: searchQuery } : null}
                      onChange={({ detail }) => {
                        setSearchQuery(detail.selectedOption.value || '');
                        setCurrentPage(1);
                      }}
                      options={existingMakes.map((m) => ({ label: m, value: m }))}
                      placeholder="Select or type make..."
                      filteringType="auto"
                    />
                  ) : searchField === 'model' ? (
                    <Select
                      selectedOption={searchQuery ? { label: searchQuery, value: searchQuery } : null}
                      onChange={({ detail }) => {
                        setSearchQuery(detail.selectedOption.value || '');
                        setCurrentPage(1);
                      }}
                      options={existingModels.map((m) => ({ label: m, value: m }))}
                      placeholder="Select or type model..."
                      filteringType="auto"
                    />
                  ) : (
                    <TextFilter
                      filteringText={searchQuery}
                      filteringPlaceholder={searchField ? `Type to search ${searchField}... (Ctrl+F)` : 'Type to search all fields... (Ctrl+F)'}
                      onChange={handleSearchChange}
                    />
                  )}
                </FormField>
              </div>
              <div style={{ minWidth: '160px' }}>
                <FormField label="Warranty">
                  <Select
                    selectedOption={
                      warrantyFilter && warrantyFilter !== 'all'
                        ? WARRANTY_FILTER_OPTIONS.find((opt) => opt.value === warrantyFilter) || { label: 'All', value: 'all' }
                        : { label: 'All', value: 'all' }
                    }
                    onChange={handleWarrantyFilterChange}
                    options={WARRANTY_FILTER_OPTIONS}
                    placeholder="All"
                  />
                </FormField>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', minHeight: '60px', paddingTop: '10px' }}>
                <Checkbox
                  checked={showOnlyStorage}
                  onChange={({ detail }) => { setShowOnlyStorage(detail.checked); setCurrentPage(1); }}
                >
                  In Storage (Fully Functional Donors Only)
                </Checkbox>
              </div>
              {hasActiveFilters && (
                <div style={{ display: 'flex', alignItems: 'center', minHeight: '60px', paddingTop: '10px' }}>
                  <Button variant="normal" iconName="undo" onClick={handleClearFilters}>
                    Reset filters
                  </Button>
                </div>
              )}
            </SpaceBetween>
          </SpaceBetween>
        </Container>

        <Tabs
          activeTabId={activeTab}
          onChange={({ detail }) => handleTabChange(detail.activeTabId)}
          tabs={[
            {
              id: 'servers',
              label: 'Servers/Encoders/Appliances',
              content: renderTable(),
            },
            {
              id: 'elementals',
              label: 'Spare Elementals',
              content: (
                <SpaceBetween size="s">
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      background: '#fff9e0',
                      border: '1px solid #f5e6a8',
                      borderRadius: 8,
                      padding: '12px 16px',
                    }}
                  >
                    <span style={{ fontSize: 16, lineHeight: '20px' }} aria-hidden="true">💡</span>
                    <div style={{ color: '#5c4d00', fontSize: 13, lineHeight: 1.6 }}>
                      <div style={{ fontWeight: 700, marginBottom: 2 }}>Model reference</div>
                      <div><strong>800s</strong> → Dell warranty code L885AE / OEMR R6525 (15th gen)</div>
                      <div><strong>900s</strong> → Dell warranty code L981AE-U / OEMR R6625/R6626 (16th gen)</div>
                    </div>
                  </div>
                  {renderTable()}
                </SpaceBetween>
              ),
            },
            {
              id: 'network_devices',
              label: 'Network Devices',
              content: renderTable(),
            },
            {
              id: 'images',
              label: `Photos (${assetsWithImages.length})`,
              content: renderImagesGallery(),
            },
            {
              id: 'bulk_add',
              label: '+ Bulk Add',
              content: <BulkAddPanel settings={settings} onBulkAdd={handleBulkAdd} onOpenCsvImport={() => setBulkImportVisible(true)} onExport={handleExportAll} />,
            },
            {
              id: 'settings',
              label: '⚙ Settings',
              content: <SettingsPanel settings={settings} onSave={handleSaveSettings} />,
            },
          ]}
        />
      </SpaceBetween>

      <Modal
        visible={addCategoryPickerVisible}
        onDismiss={() => setAddCategoryPickerVisible(false)}
        header="Add asset — select category"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setAddCategoryPickerVisible(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleAddCategoryConfirm}>
                Continue
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Tiles
          value={addCategory}
          onChange={({ detail }) => setAddCategory(detail.value as AssetCategory)}
          items={[
            {
              value: 'servers',
              label: 'Server / Encoder / Appliance',
              description: 'Dell servers, KiPros, and similar rack-mounted equipment',
            },
            {
              value: 'elementals',
              label: 'Spare Elemental',
              description: 'AWS Elemental encoder appliances (L700, L800, L900 series)',
            },
            {
              value: 'network_devices',
              label: 'Network Device',
              description: 'Cisco/Juniper switches, optics, PSUs, and fans',
            },
          ]}
        />
      </Modal>

      <AssetDetailPanel
        visible={detailPanelVisible}
        asset={selectedAsset}
        category={activeTab !== 'images' && activeTab !== 'settings' && activeTab !== 'bulk_add' ? activeTab : editingCategory}
        existingSites={existingSites}
        existingMakes={existingMakes}
        onEdit={handleEditAsset}
        onAddPhoto={(asset, newImages) => {
          const updated = { ...asset, images: newImages };
          updateAsset(editingCategory, updated as Asset);
          setSelectedAsset(updated as Asset);
        }}
        onDelete={(asset) => handleDeleteAsset(asset, editingCategory)}
        onDismiss={handleDetailPanelDismiss}
      />

      <AssetFormModal
        visible={modalVisible}
        category={editingAsset ? editingCategory : addCategory}
        asset={editingAsset}
        existingSites={existingSites}
        existingMakes={existingMakes}
        existingModels={modalModels}
        statuses={settings.statuses && settings.statuses.length > 0 ? settings.statuses : DEFAULT_ASSET_STATUSES}
        onSubmit={handleModalSubmit}
        onDismiss={handleModalDismiss}
      />

      {/* Hidden file input for direct photo upload from table */}
      <input
        ref={photoInputRef}
        type="file"
        accept="image/png,image/jpeg"
        multiple
        onChange={handlePhotoInputChange}
        style={{ display: 'none' }}
      />

      <BulkImportModal
        visible={bulkImportVisible}
        onImport={handleBulkImport}
        onDismiss={() => setBulkImportVisible(false)}
      />
    </ContentLayout>
  );
}
